import { DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ReportTabsProps, SavedReport } from './ReportTabs.props';
import { buildTabIds, splitTabIds } from './reportTabsHelpers';
import { buildTabContextItems } from './reportTabsMenuItems';

/**
 * All state, derived data, and handlers for the report tab row. Kept out of the
 * component so ReportTabs.tsx stays render-only and under the file-size limit.
 */
export const useReportTabs = ({
  activeReport,
  reports,
  onReportChange,
  onSave,
  onSetDefault,
  pinnedCodes,
  onPinnedCodesChange,
  defaultTabIndex = 0,
  onReorderTabs,
  onRevertToSystemDefaults,
  onRevertToDefaultReport,
}: ReportTabsProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmReport, setConfirmReport] = useState<SavedReport | null>(null);
  const [confirmAction, setConfirmAction] = useState<'set' | 'remove'>('set');
  const [ctxMenu, setCtxMenu] = useState<{
    pos: { top: number; left: number };
    tabId: string;
  } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Option D: viewing a report auto-pins it (persisted by the parent). The
  // current default report is excluded — it's represented by the default tab
  // (see handleConfirmDefault's consolidation), so re-pinning it would recreate
  // the duplicate tab that set-as-default removes.
  useEffect(() => {
    const defaultCode = reports.find((r) => r.isDefault)?.code;
    if (activeReport && activeReport !== defaultCode && !pinnedCodes.includes(activeReport)) {
      onPinnedCodesChange([...pinnedCodes, activeReport]);
    }
  }, [activeReport, pinnedCodes, onPinnedCodesChange, reports]);

  // Pinned reports in pinnedCodes order; drop stale codes with no matching report.
  const orderedPinned = useMemo(
    () =>
      pinnedCodes
        .map((c) => reports.find((r) => r.code === c))
        .filter((r): r is SavedReport => Boolean(r)),
    [pinnedCodes, reports]
  );
  const tabIds = useMemo(
    () =>
      buildTabIds(
        orderedPinned.map((r) => r.code),
        defaultTabIndex
      ),
    [orderedPinned, defaultTabIndex]
  );
  const currentDefault = useMemo(() => reports.find((r) => r.isDefault), [reports]);
  const hasDefaultReport = Boolean(currentDefault);

  const closeCodes = useCallback(
    (toRemove: string[]) => {
      if (toRemove.length === 0) return;
      const removeSet = new Set(toRemove);
      onPinnedCodesChange(pinnedCodes.filter((c) => !removeSet.has(c)));
      if (activeReport && removeSet.has(activeReport)) onReportChange(null);
    },
    [pinnedCodes, onPinnedCodesChange, activeReport, onReportChange]
  );

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const from = tabIds.indexOf(String(active.id));
      const to = tabIds.indexOf(String(over.id));
      if (from < 0 || to < 0) return;
      const next = splitTabIds(arrayMove(tabIds, from, to));
      if (onReorderTabs) onReorderTabs(next.pinnedCodes, next.defaultIndex);
      else onPinnedCodesChange(next.pinnedCodes);
    },
    [tabIds, onReorderTabs, onPinnedCodesChange]
  );

  const openSetDefault = useCallback((report: SavedReport) => {
    setMenuAnchor(null);
    setConfirmAction('set');
    setConfirmReport(report);
  }, []);

  const openRemoveDefault = useCallback((report: SavedReport) => {
    setMenuAnchor(null);
    setConfirmAction('remove');
    setConfirmReport(report);
  }, []);

  const handleConfirmDefault = useCallback(() => {
    if (!confirmReport || !onSetDefault) return;
    onSetDefault({ ...confirmReport, isDefault: confirmAction === 'set' });
    // Consolidate: a report made default is represented by the (renamed) default
    // tab, so drop its duplicate pinned tab. Un-setting leaves it unpinned.
    onPinnedCodesChange(pinnedCodes.filter((c) => c !== confirmReport.code));
    if (activeReport === confirmReport.code) onReportChange(null);
    setConfirmReport(null);
  }, [
    confirmReport,
    confirmAction,
    onSetDefault,
    pinnedCodes,
    onPinnedCodesChange,
    activeReport,
    onReportChange,
  ]);

  const handlePin = useCallback(
    (code: string) => {
      if (!pinnedCodes.includes(code)) onPinnedCodesChange([...pinnedCodes, code]);
      setMenuAnchor(null);
      onReportChange(code);
    },
    [pinnedCodes, onPinnedCodesChange, onReportChange]
  );

  const openCtx = useCallback((e: React.MouseEvent, tabId: string) => {
    e.preventDefault();
    setCtxMenu({ pos: { top: e.clientY, left: e.clientX }, tabId });
  }, []);

  const ctxItems = useMemo(
    () =>
      ctxMenu
        ? buildTabContextItems({
            tabId: ctxMenu.tabId,
            tabIds,
            reports,
            hasDefaultReport,
            canSetDefault: Boolean(onSetDefault),
            closeCodes,
            requestSetDefault: openSetDefault,
            onRevertToDefaultReport,
            onRevertToSystemDefaults,
            onSave,
          })
        : [],
    [
      ctxMenu,
      tabIds,
      hasDefaultReport,
      reports,
      closeCodes,
      onSetDefault,
      openSetDefault,
      onRevertToDefaultReport,
      onRevertToSystemDefaults,
      onSave,
    ]
  );

  return {
    orderedPinned,
    tabIds,
    currentDefault,
    hasDefaultReport,
    sensors,
    handleDragEnd,
    menuAnchor,
    setMenuAnchor,
    handlePin,
    openSetDefault,
    openRemoveDefault,
    ctxMenu,
    openCtx,
    setCtxMenu,
    ctxItems,
    confirmReport,
    confirmAction,
    setConfirmReport,
    handleConfirmDefault,
    closeCodes,
  };
};
